import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Info,
  Search,
  Filter,
  Loader,
  Box,
  Boxes,
  AlertTriangle,
  Ban,
  Calendar,
  Download,
  MapPin,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import galleryIcon from "../assets/img/gallery.png";
import * as XLSX from "xlsx";
import {
  getStockDetail,
  getStockMutations,
  getStocks,
} from "../api/inventoryService";
import { masterService } from "../api/masterService";
import { useNotification } from "../contexts/NotificationContext";

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function formatDateTime(value) {
  if (!value) return "-";
  try {
    return new Intl.DateTimeFormat("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

function getMutationCategoryColor(category) {
  switch ((category || "").toUpperCase()) {
    case "SALE":
    case "SALES":
    case "SALES_ORDER":
    case "PENJUALAN":
      return "text-emerald-700 ring-emerald-200 bg-emerald-50";
    case "ADJUSTMENT":
    case "PENYESUAIAN":
      return "text-orange-700 ring-orange-200 bg-orange-50";
    case "PURCHASE":
    case "PURCHASE_IN":
    case "RECEIVING":
    case "PEMBELIAN":
    case "PENERIMAAN":
      return "text-sky-700 ring-sky-200 bg-sky-50";
    default:
      return "text-slate-700 ring-slate-200 bg-slate-50";
  }
}

function getMutationCategoryLabel(item) {
  const raw = String(
    item.category ||
      item.reference_type ||
      item.source ||
      item.mutation_type ||
      "-",
  ).toUpperCase();

  switch (raw) {
    case "SALE":
    case "SALES":
    case "SALES_ORDER":
      return "Penjualan";
    case "ADJUSTMENT":
    case "PENYESUAIAN":
      return "Penyesuaian";
    case "PURCHASE":
    case "PURCHASE_IN":
    case "RECEIVING":
    case "PEMBELIAN":
    case "PENERIMAAN":
      return "Pembelian";
    default:
      return item.category || item.reference_type || raw;
  }
}

function mapMutationRow(item) {
  const qtyChange = toNumber(item.qty_change ?? item.qty ?? item.quantity ?? 0);
  const initialStock = toNumber(
    item.previous_stock ?? item.beginning_stock ?? item.stock_before ?? 0,
  );
  const isOut =
    item.type === "OUT" ||
    item.type === "CASH_OUT" ||
    item.type === "PENJUALAN" ||
    item.type === "ADJUSTMENT_OUT" ||
    qtyChange < 0;
  const absQty = Math.abs(qtyChange);
  const stockIn = !isOut ? absQty : 0;
  const stockOut = isOut ? absQty : 0;
  const finalStock = toNumber(
    item.new_stock !== undefined && item.new_stock !== null
      ? item.new_stock
      : (item.final_stock ?? item.stock_after ?? (initialStock + (isOut ? -absQty : absQty))),
  );

  return {
    id: item.id,
    category: getMutationCategoryLabel(item),
    date: formatDateTime(
      item.trx_date || item.created_at || item.date || item.mutation_date,
    ),
    notes: item.notes || "-",
    initial: initialStock,
    in: stockIn,
    out: stockOut,
    final: finalStock,
    by:
      item.created_by_name ||
      item.updated_by_name ||
      item.performed_by_name ||
      "-",
    reference: item.reference_number || item.reference_no || item.notes || "-",
    variant_id: item.variant_id || item.product_variant_id || null,
    variant_name: item.variant_name || item.variant?.name || "DEFAULT",
  };
}

export default function StockDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [stockDetail, setStockDetail] = useState(null);
  const [mutations, setMutations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [productVariants, setProductVariants] = useState([]);
  const [variantFlowMap, setVariantFlowMap] = useState(new Map());
  const [stockLocation, setStockLocation] = useState(null);
  const isRetail = localStorage.getItem("PREVIEW_APPS") !== "true";

  // Search & pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Date range filter
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const toDateInput = (d) => d.toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(toDateInput(firstOfMonth));
  const [endDate, setEndDate] = useState(toDateInput(lastOfMonth));

  // Mutation detail popup
  const [detailModal, setDetailModal] = useState({ open: false, item: null });

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");
      const [detailRes, mutRes] = await Promise.all([
        getStockDetail(id),
        getStockMutations(id, {
          limit: 999,
          start_date: startDate,
          end_date: endDate,
        }),
      ]);
      const detail = detailRes?.data || detailRes || null;
      setStockDetail(detail);
      setMutations((mutRes?.data || []).map(mapMutationRow));
      setCurrentPage(1);

      // Fetch stock location info (retail mode only)
      // inventory_stocks doesn't include stock_location_id — must fetch from master_products
      const prodIdForLocation = detail?.product_id;
      if (prodIdForLocation && localStorage.getItem("PREVIEW_APPS") !== "true") {
        masterService.products.get(prodIdForLocation)
          .then(async (prodRes) => {
            const prod = prodRes?.data || prodRes || null;
            const locId = prod?.stock_location_id;
            const rackName = prod?.rack_location;

            if (locId) {
              // Direct fetch by ID
              const locRes = await masterService.stockLocations.get(locId);
              setStockLocation(locRes?.data || locRes || null);
            } else if (rackName) {
              // Find by name from list
              const listRes = await masterService.stockLocations.list({});
              const locs = listRes?.data || listRes || [];
              const found = (Array.isArray(locs) ? locs : []).find(
                (l) => l.name === rackName
              );
              setStockLocation(found || null);
            } else {
              setStockLocation(null);
            }
          })
          .catch(() => setStockLocation(null));
      } else {
        setStockLocation(null);
      }

      // Fetch semua varian dari produk yang sama untuk Ringkasan Stok per Varian
      const productId = detail?.product_id;
      if (productId) {
        try {
          const stocksRes = await getStocks({
            limit: 100,
            product_id: productId,
          });
          const rows = stocksRes?.data || [];
          const isApps = localStorage.getItem("PREVIEW_APPS") === "true";
          let variants = rows.filter(
            (r) => r.product_id === productId || r.product_id === detail.product_id,
          );

          if (isApps) {
            variants = variants.filter((r) => !r.brand_name && !r.brand_id);
          }
          
          if (variants.length > 1) {
            variants = variants.filter(v => v.variant_id != null || v.option_id != null);
          }
          
          const variantsToUse = variants.length > 0 ? variants : [detail];
          setProductVariants(variantsToUse);

          const mutationPromises = variantsToUse.map(v => 
            getStockMutations(v.id, {
              limit: 999,
              start_date: startDate,
              end_date: endDate,
            }).catch(() => null)
          );
          const mutationResults = await Promise.all(mutationPromises);

          const flowMap = new Map();
          variantsToUse.forEach((v, idx) => {
            const vMutRes = mutationResults[idx];
            const vMuts = (vMutRes?.data || []).map(mapMutationRow);
            let inQty = 0;
            let outQty = 0;
            vMuts.forEach(m => {
              inQty += toNumber(m.in);
              outQty += toNumber(m.out);
            });
            const key = v.variant_id || v.variantId || v.option_id || v.optionId || "__main";
            flowMap.set(key, { in: inQty, out: outQty });
          });
          setVariantFlowMap(flowMap);

        } catch {
          setProductVariants(detail ? [detail] : []);
        }
      } else {
        setProductVariants(detail ? [detail] : []);
      }
    } catch (err) {
      setError(err?.message || "Gagal memuat detail stok");
      showNotification("error", err?.message || "Gagal memuat detail stok");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id, startDate, endDate]);

  const unitLabel =
    stockDetail?.unit_name ||
    stockDetail?.unit ||
    stockDetail?.product_unit_name ||
    "PCS";

  // Stats per varian untuk top summary cards
  const variantStats = useMemo(() => {
    const listToCount = productVariants.length > 0 ? productVariants : (stockDetail ? [stockDetail] : []);
    let lowCount = 0;
    let outCount = 0;
    let totalStockNum = 0;

    listToCount.forEach((v) => {
      const cur = toNumber(v.current_stock ?? v.stock ?? 0);
      const min = toNumber(v.minimum_stock ?? v.min_stock ?? v.minimumStock ?? v.minStock ?? 0);
      totalStockNum += cur;

      if (cur <= 0) {
        outCount += 1;
      } else if (min > 0 && cur <= min) {
        lowCount += 1;
      }
    });

    return {
      total: listToCount.length,
      totalStockFormatted: `${totalStockNum} ${unitLabel.toUpperCase()}`,
      lowCount,
      outCount,
    };
  }, [productVariants, stockDetail, unitLabel]);

  // Cek apakah produk memiliki varian nyata (bukan hanya DEFAULT)
  const hasRealVariants = useMemo(() => {
    return productVariants.some((v) => {
      const vName = (
        v.option_name ? `Opsi ${v.option_name}` :
        v.variant_name ||
        v.name ||
        "DEFAULT"
      )
        .toString()
        .toUpperCase();
      return vName !== "DEFAULT";
    });
  }, [productVariants]);

  const periodFlowByVariant = variantFlowMap;

  // Filter + paginate mutations
  const filteredMutations = useMemo(() => {
    if (!searchQuery.trim()) return mutations;
    const q = searchQuery.toLowerCase();
    return mutations.filter(
      (m) =>
        m.category.toLowerCase().includes(q) ||
        m.by.toLowerCase().includes(q) ||
        m.date.toLowerCase().includes(q) ||
        (m.variant_name || "").toLowerCase().includes(q),
    );
  }, [mutations, searchQuery]);

  const totalEntries = filteredMutations.length;
  const maxPages = Math.max(1, Math.ceil(totalEntries / itemsPerPage));
  const startEntry =
    totalEntries === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endEntry = Math.min(currentPage * itemsPerPage, totalEntries);
  const pagedMutations = filteredMutations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const getVariantImageUrl = (variant) => {
    const rawUrl =
      variant?.image_url ||
      variant?.product_image ||
      variant?.image ||
      variant?.photo_url ||
      stockDetail?.image_url ||
      stockDetail?.product_image ||
      stockDetail?.image;

    if (!rawUrl) return galleryIcon;
    if (rawUrl.startsWith("http")) return rawUrl;
    const apiBase = import.meta.env.VITE_API_URL || "";
    return `${apiBase}/storage/${rawUrl}`;
  };

  const handleExportExcel = () => {
    if (!filteredMutations || filteredMutations.length === 0) {
      showNotification("error", "Tidak ada data mutasi stok untuk di-export.");
      return;
    }

    const productName = stockDetail?.product_name || stockDetail?.name || "Produk";
    const now = new Date();
    const printDate = now.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    const excelData = [
      ["LAPORAN RIWAYAT MUTASI STOK PERSPEKTIF PERSEDIAAN"],
      [`Nama Produk: ${productName}`],
      [`Periode Mutasi: ${startDate} s/d ${endDate}`],
      [`Waktu Export: ${printDate}`],
      [],
      ["--- RINGKASAN STATUS STOK ---"],
      ["Total Stok Saat Ini", variantStats.totalStockFormatted],
      ["Total Varian Terdaftar", variantStats.total],
      ["Jumlah Varian Menipis", variantStats.lowCount],
      ["Jumlah Varian Habis", variantStats.outCount],
      [],
      [
        "No",
        "Tanggal Mutasi",
        "Varian",
        "Kategori Mutasi",
        "Stok Awal",
        "Stok Masuk",
        "Stok Keluar",
        "Stok Akhir",
        "Dibuat Oleh",
        "Catatan / Referensi",
      ],
    ];

    let sumIn = 0;
    let sumOut = 0;

    filteredMutations.forEach((item, idx) => {
      sumIn += Number(item.in || 0);
      sumOut += Number(item.out || 0);

      excelData.push([
        idx + 1,
        item.date || "-",
        item.variant_name || "DEFAULT",
        item.category || "-",
        item.initial,
        item.in,
        item.out,
        item.final,
        item.by || "-",
        item.reference || item.notes || "-",
      ]);
    });

    // Baris Total
    excelData.push([
      "TOTAL PERIODE",
      "",
      "",
      "",
      "",
      sumIn,
      sumOut,
      "",
      "",
      "",
    ]);

    const ws = XLSX.utils.aoa_to_sheet(excelData);

    ws["!cols"] = [
      { wch: 6 },
      { wch: 22 },
      { wch: 18 },
      { wch: 18 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 24 },
      { wch: 30 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Mutasi Stok");

    const safeName = productName.replace(/[^a-zA-Z0-9_-]/g, "_");
    const fileName = `Mutasi_Stok_${safeName}_${startDate}_sd_${endDate}.xlsx`;
    XLSX.writeFile(wb, fileName);
    showNotification("success", `Laporan excel ${fileName} berhasil diunduh!`);
  };

  return (
    <div className="flex h-screen bg-slate-50 font-sans">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() =>
          setIsSidebarCollapsed((prev) => {
            const newState = !prev;
            localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
            return newState;
          })
        }
        activePage="inventori"
      />

      <div
        className={`flex-1 overflow-auto transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-48"}`}>
        <Header />

        <div className="p-4 sm:p-6 lg:p-8 space-y-5 sm:space-y-6">
          {/* === Hero Header Banner (Responsive & Mobile-Proof) === */}
          <div className="relative bg-teal-700 rounded-2xl p-3.5 sm:p-5 overflow-hidden shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => navigate(-1)}
                aria-label="Kembali"
                title="Kembali"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-all shrink-0">
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <div className="min-w-0 flex-1">
                {loading ? (
                  <div className="h-6 w-48 sm:w-64 bg-white/20 rounded animate-pulse" />
                ) : (
                  <h1 className="text-base sm:text-xl lg:text-2xl font-bold text-white tracking-tight leading-snug break-words line-clamp-2">
                    {stockDetail?.product_name || stockDetail?.name || "Detail Persediaan Stok"}
                  </h1>
                )}
              </div>
            </div>

            {/* Date Range Picker Filter (Mobile Responsive & Tight Gap) */}
            <div className="flex items-center gap-2 w-full sm:w-auto shrink-0">
              <div className="flex-1 sm:flex-initial flex items-center justify-between sm:justify-start gap-1 sm:gap-2 bg-[#095751] border border-white/20 rounded-xl px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs text-white shadow-xs min-w-0">
                <Calendar className="w-4 h-4 text-white/80 shrink-0" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="outline-none bg-transparent text-white text-[11px] sm:text-xs w-full max-w-[105px] sm:w-28 [color-scheme:dark] cursor-pointer shrink min-w-0"
                />
                <span className="text-white/60 font-semibold shrink-0">-</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="outline-none bg-transparent text-white text-[11px] sm:text-xs w-full max-w-[105px] sm:w-28 [color-scheme:dark] cursor-pointer shrink min-w-0"
                />
              </div>
              <button
                type="button"
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-all shrink-0"
                title="Filter">
                <Filter className="w-4 h-4" />
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-rose-50 ring-1 ring-rose-200 rounded-xl px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          {/* === Stat Cards Row (4 cards + optional Lokasi Stok) === */}
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${
            isRetail && stockLocation ? "lg:grid-cols-5" : "lg:grid-cols-4"
          }`}>
            <StatCard
              label="TOTAL STOK SAAT INI"
              value={variantStats.totalStockFormatted}
              hint="Total seluruh varian"
              icon={<Boxes className="w-5 h-5" />}
              iconBg="bg-emerald-50 border border-emerald-100"
              iconColor="text-emerald-600"
              loading={loading}
            />
            <StatCard
              label="TOTAL VARIAN"
              value={variantStats.total}
              hint="Varian terdaftar"
              icon={<Box className="w-5 h-5" />}
              iconBg="bg-emerald-50 border border-emerald-100"
              iconColor="text-emerald-600"
              loading={loading}
            />
            <StatCard
              label="VARIAN MENIPIS"
              value={variantStats.lowCount}
              hint="Di bawah minimum stok"
              icon={<AlertTriangle className="w-5 h-5" />}
              iconBg="bg-amber-50 border border-amber-100"
              iconColor="text-amber-600"
              loading={loading}
            />
            <StatCard
              label="VARIAN HABIS"
              value={variantStats.outCount}
              hint="Stok = 0"
              icon={<Ban className="w-5 h-5" />}
              iconBg="bg-rose-50 border border-rose-100"
              iconColor="text-rose-600"
              loading={loading}
            />

            {/* Card ke-5: Lokasi Stok (Retail Mode Only) */}
            {isRetail && stockLocation && (
              <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-4 flex flex-col justify-between gap-3">
                <div className="flex items-start justify-between gap-2">
                  {/* Icon / Thumbnail */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 overflow-hidden border ${
                    stockLocation.image_url && stockLocation.image_url.trim() !== ""
                      ? "border-slate-200 bg-slate-100"
                      : "bg-teal-50 border-teal-100"
                  }`}>
                    {stockLocation.image_url && stockLocation.image_url.trim() !== "" ? (
                      <img
                        src={stockLocation.image_url}
                        alt={stockLocation.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <MapPin className="w-5 h-5 text-teal-600" />
                    )}
                  </div>

                  {/* Status badge */}
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold shrink-0 ${
                    stockLocation.is_active
                      ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                      : "bg-rose-50 text-rose-600 ring-1 ring-rose-200"
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${stockLocation.is_active ? "bg-emerald-500" : "bg-rose-400"}`} />
                    {stockLocation.is_active ? "Aktif" : "Non-Aktif"}
                  </span>
                </div>

                <div className="min-w-0">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">LOKASI STOK</p>
                  <p className="text-sm font-bold text-slate-800 truncate">{stockLocation.name}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    {stockLocation.type && (
                      <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-teal-50 text-teal-700">
                        {stockLocation.type}
                      </span>
                    )}
                    {stockLocation.description && (
                      <span className="text-[10px] text-slate-400 truncate">{stockLocation.description}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>


          {/* === Ringkasan Stok per Varian === */}
          {hasRealVariants && (
            <div className="space-y-4">
              <h2 className="text-base font-bold text-slate-900 tracking-tight">
                Ringkasan Stok per Varian
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {productVariants
                  .filter((variant) => {
                    const variantName = (
                      variant.option_name ? `Opsi ${variant.option_name}` :
                      variant.variant_name ||
                      variant.name ||
                      "DEFAULT"
                    )
                      .toString()
                      .toUpperCase();
                    return variantName !== "DEFAULT";
                  })
                  .map((variant) => {
                    const rawName = (
                      variant.option_name ? variant.option_name :
                      variant.variant_name ||
                      variant.name ||
                      "Default"
                    ).toString();

                    const variantStock = toNumber(
                      variant.current_stock ?? variant.stock ?? 0,
                    );
                    const variantMin = toNumber(
                      variant.minimum_stock ?? variant.min_stock ?? 0,
                    );
                    const variantUnit =
                      variant.unit_name || variant.unit || unitLabel;
                    const isOut = variantStock <= 0;
                    const isLow =
                      !isOut && variantMin > 0 && variantStock <= variantMin;
                    const statusLabel = isOut
                      ? "Habis"
                      : isLow
                        ? "Menipis"
                        : "Aman";
                    const statusDotCls = isOut
                      ? "bg-rose-500"
                      : isLow
                        ? "bg-amber-500"
                        : "bg-emerald-500";
                    const statusBadgeCls = isOut
                      ? "bg-rose-50 text-rose-600 border-rose-200/80"
                      : isLow
                        ? "bg-amber-50 text-amber-600 border-amber-200/80"
                        : "bg-emerald-50 text-emerald-600 border-emerald-200/80";

                    const target =
                      variantMin > 0 ? variantMin * 2 : Math.max(variantStock, 1);
                    const pct = Math.min(
                      100,
                      Math.max(0, (variantStock / target) * 100),
                    );
                    const barColor = isOut
                      ? "bg-rose-500"
                      : isLow
                        ? "bg-amber-500"
                        : "bg-emerald-500";
                    const flow = periodFlowByVariant.get(variant.variant_id || variant.variantId) ||
                      periodFlowByVariant.get(variant.option_id || variant.optionId) ||
                      periodFlowByVariant.get("__main") || { in: 0, out: 0 };
                    const imgUrl = getVariantImageUrl(variant);

                    return (
                      <div
                        key={variant.id}
                        className="bg-white rounded-2xl border border-slate-200/80 p-4 shadow-xs hover:shadow-md hover:border-teal-200 transition-all flex flex-col justify-between space-y-3">
                        {/* Header Row: Thumbnail + Variant Name + Status Badge */}
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 rounded-xl bg-slate-100 border border-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                            <img
                              src={imgUrl}
                              alt={rawName}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = galleryIcon;
                              }}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <h3 className="text-sm font-bold text-slate-900 truncate">
                                {rawName}
                              </h3>
                              <span
                                className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-xs font-semibold shrink-0 ${statusBadgeCls}`}>
                                <span
                                  className={`w-1.5 h-1.5 rounded-full ${statusDotCls}`}
                                />
                                {statusLabel}
                              </span>
                            </div>

                            <div className="flex items-baseline justify-between mt-2">
                              <div>
                                <p className="text-[10px] font-medium text-slate-400">
                                  Stok Saat Ini
                                </p>
                                <p className="text-base font-bold text-slate-900">
                                  {variantStock}{" "}
                                  <span className="text-xs font-normal text-slate-500">
                                    {variantUnit}
                                  </span>
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-[10px] font-medium text-slate-400">
                                  Minimum Stok
                                </p>
                                <p className="text-xs font-semibold text-slate-700">
                                  {variantMin}{" "}
                                  <span className="text-[10px] font-normal text-slate-500">
                                    {variantUnit}
                                  </span>
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Progress bar */}
                        <div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${barColor} transition-all duration-500`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <div className="flex justify-between text-[10px] font-medium text-slate-400 mt-1">
                            <span>0%</span>
                            <span>50%</span>
                            <span>100%</span>
                          </div>
                        </div>

                        {/* Bottom Stats: Stok Masuk & Stok Keluar */}
                        <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-100">
                          <div>
                            <span className="text-slate-400">Stok Masuk (Periode)</span>
                            <p className="font-bold text-emerald-600">
                              {flow.in} {variantUnit}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="text-slate-400">Stok Keluar (Periode)</span>
                            <p className="font-bold text-rose-500">
                              {flow.out} {variantUnit}
                            </p>
                          </div>
                        </div>

                        {/* Tombol Lihat Riwayat per Varian */}
                        {variant.id && variant.id !== stockDetail?.id && (
                          <button
                            type="button"
                            onClick={() => navigate(`/inventori/stok/${variant.id}`)}
                            className="w-full py-1.5 rounded-xl bg-slate-50 hover:bg-teal-50 border border-slate-200 hover:border-teal-200 text-xs font-semibold text-slate-500 hover:text-teal-700 transition-all flex items-center justify-center gap-1.5 mt-1">
                            <Info className="w-3.5 h-3.5" />
                            Lihat Riwayat
                          </button>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* === Section: Riwayat Mutasi Stok === */}
          <div className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-xs">
            {/* Table Header Row */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 border-b border-slate-100">
              <h2 className="text-base font-bold text-slate-900 tracking-tight">
                Riwayat Mutasi Stok
              </h2>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Cari data . . ."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors w-56"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleExportExcel}
                  title="Export Data ke Excel"
                  className="w-9 h-9 border border-amber-200/80 rounded-xl flex items-center justify-center bg-amber-50 text-amber-600 hover:bg-amber-100 transition shadow-2xs">
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-50/80 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  <tr>
                    <th className="px-6 py-3.5 w-16">AKSI</th>
                    <th className="px-6 py-3.5">TANGGAL MUTASI</th>
                    <th className="px-6 py-3.5">VARIAN</th>
                    <th className="px-6 py-3.5">KATEGORI</th>
                    <th className="px-6 py-3.5 text-center">STOK AWAL</th>
                    <th className="px-6 py-3.5 text-center">STOK MASUK</th>
                    <th className="px-6 py-3.5 text-center">STOK KELUAR</th>
                    <th className="px-6 py-3.5 text-center">STOK AKHIR</th>
                    <th className="px-6 py-3.5">DIBUAT OLEH</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-xs">
                  {loading ? (
                    <tr>
                      <td
                        colSpan="9"
                        className="px-6 py-12 text-center text-slate-400">
                        <div className="flex items-center justify-center gap-2">
                          <Loader className="w-4 h-4 animate-spin text-teal-600" />{" "}
                          Memuat data...
                        </div>
                      </td>
                    </tr>
                  ) : pagedMutations.length === 0 ? (
                    <tr>
                      <td
                        colSpan="9"
                        className="px-6 py-12 text-center text-slate-400 font-medium">
                        Tidak ada riwayat mutasi stok
                      </td>
                    </tr>
                  ) : (
                    pagedMutations.map((item) => (
                      <tr
                        key={item.id}
                        className="hover:bg-slate-50/80 transition-colors">
                        <td className="px-6 py-3.5">
                          <button
                            type="button"
                            onClick={() => setDetailModal({ open: true, item })}
                            className="w-7 h-7 bg-teal-600 text-white rounded-lg flex items-center justify-center hover:bg-teal-700 transition-colors shadow-2xs"
                            title="Detail Mutasi">
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </td>
                        <td className="px-6 py-3.5 font-medium text-slate-600">
                          {item.date}
                        </td>
                        <td className="px-6 py-3.5 font-bold text-slate-800 uppercase">
                          {item.variant_name || "DEFAULT"}
                        </td>
                        <td className="px-6 py-3.5">
                          <span
                            className={`inline-flex px-2.5 py-0.5 rounded-full border text-[11px] font-semibold ${getMutationCategoryColor(item.category)}`}>
                            {item.category}
                          </span>
                        </td>
                        <td className="px-6 py-3.5 text-center font-medium text-slate-700">
                          {item.initial}
                        </td>
                        <td className="px-6 py-3.5 text-center font-bold text-emerald-600">
                          {item.in}
                        </td>
                        <td className="px-6 py-3.5 text-center font-bold text-rose-500">
                          {item.out}
                        </td>
                        <td className="px-6 py-3.5 text-center font-bold text-slate-900">
                          {item.final}
                        </td>
                        <td className="px-6 py-3.5 font-medium text-slate-600">
                          {item.by}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Footer */}
            {!loading && (
              <div className="px-6 py-3.5 border-t border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-slate-600">
                <div className="flex items-center gap-2">
                  <span>
                    Showing {startEntry} to {endEntry} of {totalEntries} entries
                  </span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="ml-2 bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500">
                    {[10, 25, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold">
                    «
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold">
                    ‹
                  </button>
                  {Array.from({ length: Math.min(maxPages, 5) }, (_, i) => {
                    const page =
                      Math.max(1, Math.min(maxPages - 4, currentPage - 2)) + i;
                    return (
                      <button
                        key={page}
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-semibold transition ${page === currentPage ? "bg-teal-600 text-white shadow-xs" : "bg-white border border-slate-200 hover:bg-slate-50"}`}>
                        {page}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(maxPages, p + 1))
                    }
                    disabled={currentPage === maxPages}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold">
                    ›
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(maxPages)}
                    disabled={currentPage === maxPages}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold">
                    »
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mutation Detail Modal */}
      {detailModal.open && detailModal.item && (
        <div
          onClick={() => setDetailModal({ open: false, item: null })}
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm modal-backdrop-enter">
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-md mx-4 overflow-hidden modal-panel-enter">
            <div className="flex items-center justify-between px-6 py-4 bg-teal-600 text-white">
              <h3 className="text-base font-semibold tracking-tight">
                Detail Mutasi Stok
              </h3>
              <button
                type="button"
                onClick={() => setDetailModal({ open: false, item: null })}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:bg-white/10 transition">
                ✕
              </button>
            </div>
            <div className="px-6 py-5 space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-slate-500">Kategori</span>
                <span
                  className={`inline-flex px-2.5 py-0.5 rounded-full border text-[11px] font-semibold ${getMutationCategoryColor(detailModal.item.category)}`}>
                  {detailModal.item.category}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Tanggal</span>
                <span className="font-medium text-slate-800">
                  {detailModal.item.date}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Stok Awal</span>
                <span className="font-medium text-slate-800">
                  {detailModal.item.initial} {unitLabel}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Stok Masuk</span>
                <span className="font-semibold text-emerald-600">
                  +{detailModal.item.in}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Stok Keluar</span>
                <span className="font-semibold text-rose-500">
                  -{detailModal.item.out}
                </span>
              </div>
              <div className="flex justify-between bg-teal-50 border border-teal-200/80 rounded-xl px-3 py-2">
                <span className="text-teal-700">Stok Akhir</span>
                <span className="font-bold text-teal-900">
                  {detailModal.item.final} {unitLabel}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Dibuat oleh</span>
                <span className="font-medium text-slate-800">
                  {detailModal.item.by}
                </span>
              </div>
              {detailModal.item.notes && detailModal.item.notes !== "-" && (
                <div className="flex flex-col gap-1">
                  <span className="text-slate-500">Keterangan</span>
                  <span className="font-medium text-slate-800 text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                    {detailModal.item.notes}
                  </span>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/40">
              <button
                type="button"
                onClick={() => setDetailModal({ open: false, item: null })}
                className="w-full py-2 rounded-xl bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition">
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  valueSuffix,
  hint,
  icon,
  iconBg = "bg-teal-50",
  iconColor = "text-teal-600",
  loading,
}) {
  return (
    <div className="p-4 bg-white rounded-2xl border border-slate-200/80 shadow-xs flex items-center gap-3.5">
      <div
        className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${iconBg} ${iconColor}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">
          {label}
        </p>
        {loading ? (
          <div className="h-6 w-20 bg-slate-100 rounded animate-pulse" />
        ) : (
          <p className="text-xl font-bold text-slate-800 leading-tight">
            {value}
            {valueSuffix && (
              <span className="ml-1 text-xs font-normal text-slate-500">
                {valueSuffix}
              </span>
            )}
          </p>
        )}
        {hint && (
          <p className="text-[11px] text-slate-400 mt-0.5 truncate">{hint}</p>
        )}
      </div>
    </div>
  );
}
